import { Component, OnInit, Input } from '@angular/core';
import { buildEvaluationStatus, RecentHistoryItem, EvalHistoryItemType, HistoryItemType, EvaluationVehicle, EvaluationDestination, StatusEvaluation, EvaluationStatusSelectedOptions, EvalReason, ReasonsArchivesLabel, NegotiationStatusTypes, MotorcycleType } from '@app-model/evaluation';
import { UserInfo } from '@app/store/session/session.store';
import { EvaluationService } from '@app-services/evaluation.service';
import { DatePipe, DecimalPipe, CurrencyPipe } from '@angular/common';
import { StringUtils } from '@app-utils/StringUtils';
import { ReportStatus, FuelEnum, TransmissionEnum, Color, CountryOrigin, EquipmentClassification, TruckTractions, TruckClassifications, TruckType, ImplementTypeName } from '@app-model/vehicle';
import { toPriceDisplayValue } from '@app-utils/eval-price.utils';

@Component({
  selector: 'app-evaluation-history',
  templateUrl: './evaluation-history.component.html',
  styleUrls: ['./evaluation-history.component.scss']
})
export class EvaluationHistoryComponent implements OnInit {
  @Input() vehicle: EvaluationVehicle;
  @Input() userInfo: UserInfo;

  numHistoryItems = 0;
  totalHistoryItems = 0;
  recentHistoryItems: RecentHistoryItem[];

  showMore = true;

  private datePipe = new DatePipe('pt-BR');
  private currencyPipe = new CurrencyPipe('pt-BR');

  constructor(private evaluationService: EvaluationService, private decimalPipe: DecimalPipe) { }

  ngOnInit() {
    this.numHistoryItems = this.vehicle.numHistoryItems - (this.vehicle.recentHistoryItems?.length || 0);
    this.recentHistoryItems = this.vehicle.recentHistoryItems;
  }

  getFrom({ userName, userId }: RecentHistoryItem) {
    if (this.isYou({ userId })) return 'Você';
    if (userName == null || userName === '') return 'Sistema';
    const spaceIndex = userName.indexOf(' ');
    return spaceIndex === -1 ? userName : userName.substring(0, spaceIndex);
  }

  isYou({ userId }) {
    return this.userInfo.id === userId;
  }

  getSecondaryText(item: RecentHistoryItem) {
    const { type, diff } = item;
    if (diff?.some((d: any) => d.fieldName === 'trimName')) {
      return 'alterou a versão';
    }
    if (type === HistoryItemType.UPDATED_PRICING) {
      return EvalHistoryItemType.UPDATED;
    }
    if (type === HistoryItemType.EDITED_EVALUATION_COMMENT) {
      const isPrivate = this.getEditedCommentIsPrivate(item);
      const suffix = isPrivate === true ? ' (restrita)' : isPrivate === false ? ' (geral)' : '';
      return EvalHistoryItemType.EDITED_EVALUATION_COMMENT + suffix;
    }
    if (type === HistoryItemType.SCHEDULED) {
      const datePipe = new DatePipe('pt-BR');
      return EvalHistoryItemType[type] + ' para ' + datePipe.transform(this.vehicle.scheduledDate, 'dd/MM/yyyy');
    }
    // if (type === HistoryItemType.ARCHIVED) {
    //   return EvalHistoryItemType[type] + ' pelo motivo: ' + this.getReasonArchive(diff[0].newValue);
    // }
    return EvalHistoryItemType[type];
  }

  getDurationText(item: RecentHistoryItem): string | null {
    const { type } = item;
    if (type === HistoryItemType.CLOSED_PRICING || type === HistoryItemType.CLOSED_EVAL || type === HistoryItemType.SENT_TO_EVAL || type === HistoryItemType.BOUGHT) {
      const duration = this.extractPricingDuration(item);
      if (duration != null && duration > 0) {
        return `Duração da precificação · ${this.formatDuration(duration)}`;
      }
    }
    return null;
  }

  private extractPricingDuration(item: RecentHistoryItem): number | null {
  
    if (item.pricingDurationMinutes != null) {
      return item.pricingDurationMinutes;
    }

    const diff = item.diff;

    if (diff?.length) {
      const durationDiff = diff.find((d: any) => d.fieldName === 'pricingDurationMinutes');
      if (durationDiff?.newValue != null) {
        return Number(durationDiff.newValue);
      }
    }

    const endDate = this.getPricingEndDate(item);
    if (endDate && this.recentHistoryItems?.length) {
      const startItem = this.findMostRecentStartedPricing(item);
      if (startItem?.createdAt) {
        const diffMs = endDate.getTime() - new Date(startItem.createdAt).getTime();
        if (diffMs > 0) {
          return diffMs / 60000;
        }
      }
    }

    if (this.vehicle.pricingDurationMinutes != null) {
      return this.vehicle.pricingDurationMinutes;
    }

    return null;
  }

  private getPricingEndDate(item: RecentHistoryItem): Date | null {
    // Tenta pegar pricingEndedAt do diff
    const endDiff = item.diff?.find((d: any) => d.fieldName === 'pricingEndedAt');
    if (endDiff?.newValue) {
      return new Date(endDiff.newValue);
    }
    // Usa o createdAt do próprio item CLOSED_PRICING como fallback
    if (item.createdAt) {
      return new Date(item.createdAt);
    }
    return null;
  }

  private findMostRecentStartedPricing(closedItem: RecentHistoryItem): RecentHistoryItem | null {
    const closedTime = new Date(closedItem.createdAt).getTime();

    let best: RecentHistoryItem | null = null;
    let bestTime = 0;
    for (const h of this.recentHistoryItems) {
      if (h.type === HistoryItemType.STARTED_PRICING && h.createdAt) {
        const t = new Date(h.createdAt).getTime();
        if (t < closedTime && t > bestTime) {
          best = h;
          bestTime = t;
        }
      }
    }
    return best;
  }

  private formatDuration(minutes: number): string {
    const totalMinutes = Math.floor(minutes);
    if (totalMinutes < 60) {
      return `${totalMinutes} min`;
    }
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }

  onClickFullHistory() {
    this.evaluationService.fullHistory(this.vehicle.id).subscribe(response => {
      this.showMore = false;
      this.recentHistoryItems = response;
    });
  }

  get status() {
    return buildEvaluationStatus(this.vehicle);
  }

  getInitials({ userName }: RecentHistoryItem) {
    return StringUtils.getInitialsName(userName ?? '');
  }

  getEditedCommentDiff(item: RecentHistoryItem): { previousText: string; newText: string } | null {
    if (item.type !== HistoryItemType.EDITED_EVALUATION_COMMENT || !item.diff?.length) {
      return null;
    }

    const commentDiff = item.diff.find((d: any) => d.fieldName === 'comment');
    if (!commentDiff) return null;

    return {
      previousText: commentDiff.originalValue ?? '',
      newText: commentDiff.newValue ?? '',
    };
  }

  getEditedCommentIsPrivate(item: RecentHistoryItem): boolean | null {
    if (item.type !== HistoryItemType.EDITED_EVALUATION_COMMENT || !item.diff?.length) {
      return null;
    }
    const isPrivateDiff = item.diff.find((d: any) => d.fieldName === 'isPrivate');
    if (isPrivateDiff?.newValue == null) return null;
    return isPrivateDiff.newValue === true || isPrivateDiff.newValue === 'true';
  }

  getReasonArchive(reason: string) {
    switch (reason) {
      case "BUSINESS_NO_EXCHANGE": return "não deu negócio"; break;
      case "NO_RETURN": return "sem retorno"; break;
      case "CREDIT_REPROVED": return "crédito reprovado"; break;
      case "CUSTOMER_GAVE_UP": return "cliente desistiu"; break;
      case "PURCHASED_FROM_ANOTHER_DEALER": return "comprou em outra concessionária"; break;
      case "PRECAUTIONARY_REPROVED": return "cautelar reprovada"; break;
      case "PRICING": return "precificação"; break;
      case "THIRD_PARTY_SALE": return "venda de terceiros"; break;
      case "PRODUCT_UNAVAILABLE": return "produto indisponível"; break;
      case "ONLINE_ASSESSMENT": return "avaliação online"; break;
    }
  }

  getDiffItems(item: RecentHistoryItem): { fieldLabel: string; displayText: string }[] {
    if (item.type === HistoryItemType.EDITED_EVALUATION_COMMENT || !item.diff?.length) {
      return [];
    }
    return item.diff
      .filter(d => this.isFieldAllow(d.fieldName))
      .map(d => {
        const fieldLabel = this.getFiedName(d.fieldName);
        const oldValue = this.formatValue(d.fieldName, d.originalValue ?? d.oldValue);
        const newValue = this.formatValue(d.fieldName, d.newValue);
        const displayText = oldValue && oldValue !== 'Não informado'
          ? `${oldValue} → ${newValue}`
          : newValue;
        return { fieldLabel, displayText };
      });
  }

  private formatValue(fieldName: string, value: any): string {
    if (value === null || value === undefined || value === '') {
      return 'Não informado';
    }
    return this.getNewValue(fieldName, value);
  }

  isFieldAllow(fieldName: string) {

    if (fieldName && (fieldName.endsWith('Id') || fieldName.endsWith('Ids'))) {
      return false;
    }
    switch (fieldName) {
      case 'status':
      case 'createdAt':
      case 'pricingStartedAt':
      case 'pricingEndedAt':
      case 'pricingUserName':
      case 'pricingUserId':
      case 'pricingDurationMinutes':
      case 'pricingHistory':
      case 'lastDeskPrice':
      case 'interventionPriceAccepted':
      case 'reportItems':
      case 'updatedAt':
      case 'lastStatusUpdate':
      case 'boughtAt':
      case 'images':
      case 'sellerManagerIdPriceUpdate':
      case 'audio':
      case 'reportUrl':
      case 'reportTitle':
      case 'estimatedPrice':
      case 'inFastQuote':
      case 'firstDestination':
      case 'firstReturnedToPricingAt':
      case 'identifierKeys':
      case 'numberPlate':
      case 'chassis':
        return false;
      default:
        return true;
    }

  }
  getFiedName(value: string): string {
    const fieldLabels: { [key: string]: string } = {
      'status': 'Status',
      'currentEvaluatorId': 'Avaliador',
      'destination': 'Destino',
      'listPrice': 'Preço p/ anúncio',
      'minSellPrice': 'Preço mínimo',
      'factoryBonus': 'Bônus de fábrica',
      'interventionPrice': 'Intervenção',
      'price': 'Preço avaliado arredondado',
      'priceCent': 'Preço avaliado com centavos',
      'doors': 'Portas',
      'renavam': 'Renavam',
      'reportItems': 'Itens do laudo',
      'evalDealerId': 'Unidade',
      'images': 'Fotos',
      'evalReason': 'Motivo da captação',
      'otherEvalReason': 'Outro motivo da captação',
      'owner': 'Proprietário',
      'owner.phoneNumber': 'Telefone',
      'reasonArchive': 'Motivo do arquivamento',
      'finalEvalScore': 'Nota final',
      'finalEvalScoreABC': 'Nota final (ABC)',
      'carManual': 'Manual do carro',
      'extraKey': 'Chave reserva',
      'registrationCity': 'Cidade de registro',
      'chassis': 'Chassi',
      'bodywork': 'Carroceria',
      'power': 'Potência',
      'passengers': 'Passageiros',
      'segment': 'Segmento',
      'reportStatus': 'Laudo cautelar',
      'tireReports': 'Estado dos pneus',
      'km': 'Quilometragem',
      'color': 'Cor',
      'fuel': 'Combustível',
      'transmission': 'Câmbio',
      'countryOrigin': 'Origem',
      'numberPlate': 'Placa',
      'manufactureYear': 'Ano de fabricação',
      'modelYear': 'Ano do modelo',
      'fipePrice': 'Preço FIPE',
      'expectedPrice': 'Expectativa de preço',
      'negotiationStatus': 'Status da negociação',
      'comments': 'Comentários',
      'commentsForPricing': 'Comentários p/ precificação',
      'description': 'Descrição',
      'makeId': 'Marca',
      'makeName': 'Marca',
      'modelId': 'Modelo',
      'modelName': 'Modelo',
      'trimId': 'Versão',
      'trimName': 'Versão',
      'cityId': 'Cidade',
      'city': 'Cidade',
      'uf': 'UF',
      'equipmentIds': 'Equipamentos',
      'originalEquipmentIds': 'Equipamentos originais',
      'scheduledDate': 'Data agendada',
      'expirationDate': 'Data de expiração',
      'hasTradeIn': 'Possui troca',
      'hasVideo': 'Possui vídeo',
      'axleAndTire': 'Eixos e pneus',
      'tiresDimension': 'Dimensão dos pneus',
      'wheelbase': 'Distância entre eixos',
      'gearsSize': 'Número de marchas',
      'tankSize': 'Capacidade do tanque',
      'truckCabin': 'Cabine',
      'truckClassificationLabel': 'Classificação do caminhão',
      'truckTractionLabel': 'Tração do caminhão',
      'truckTypeLabel': 'Tipo do caminhão',
      'implement': 'Implemento',
      'motorcycleType': 'Tipo de moto',
      'questions': 'Perguntas da avaliação',
      'webPrice': 'Preço web',
      'pluginRaioXPrice': 'Preço RaioX',
      'dossierPrice': 'Preço +RaioX',
      'dossierStatus': 'Status +RaioX',
      'agreedPrice': 'Preço acordado',
      'bestBid': 'Melhor lance',
      'leadPrice': 'Preço do lead',
      'grossMargin': 'Margem bruta',
      'margin': 'Margem',
      'stockDays': 'Dias em estoque',
      'inInventory': 'Entrou para o estoque',
      'tradeInMarketPrice': 'Valor de mercado',
      'tradeInDealerCosts': 'Custos do lojista',
      'tradeInExpectedPrice': 'Valor da calculadora'
    };
    return fieldLabels[value] || this.formatFieldName(value);
  }

  private formatFieldName(fieldName: string): string {
    return fieldName?.replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  getNewValue(fieldName: string, value: any): string {
    if (value === null || value === undefined || value === '') {
      return 'Não informado';
    }

    switch (fieldName) {
      case 'destination':
        return EvaluationDestination[value] || value;

      case 'status':
        return this.getStatusLabel(value);

      case 'reportItems':
        return this.formatReportItems(value);

      case 'evalDealerId':
        return value === this.vehicle.evalDealerId ? this.vehicle.evalDealerName : value;

      case 'images':
        if (Array.isArray(value)) {
          return value.length > 0 ? `${value.length} foto(s)` : 'Sem fotos';
        }
        return 'Fotos atualizadas';

      case 'evalReason':
        return EvalReason.getLabel(value);

      case 'otherEvalReason':
        return value;

      case 'owner':
        return this.formatOwner(value);

      case 'reasonArchive':
        return ReasonsArchivesLabel.getLabel(value);

      case 'finalEvalScore':
        return typeof value === 'number' ? value.toFixed(1) : value;

      case 'finalEvalScoreABC':
        return value;

      case 'carManual':
      case 'extraKey':
      case 'hasTradeIn':
      case 'hasVideo':
      case 'fillPriceBeforePurchase':
      case 'defaultCompetitiveFastQuote':
      case 'blockEvalReturnToDeskBuy':
        return value ? 'Sim' : 'Não';

      case 'segment':
        return this.getSegmentLabel(value);

      case 'reportStatus':
        return ReportStatus[value] || value;

      case 'tireReports':
        return this.formatTireReports(value);

      case 'fuel':
        return FuelEnum[value] || value;

      case 'transmission':
        return TransmissionEnum[value] || value;

      case 'color':
        return Color[value] || value;

      case 'countryOrigin':
        return CountryOrigin[value] || value;

      case 'negotiationStatus':
        return NegotiationStatusTypes.getLabel(value) || value;

      case 'motorcycleType':
        return MotorcycleType.getLabel(value) || value;

      case 'trimName':
        return String(value);

      case 'priceCent':
        return this.formatCurrencyCent(value);

      case 'price':
      case 'listPrice':
      case 'minSellPrice':
      case 'factoryBonus':
      case 'interventionPrice':
      case 'fipePrice':
      case 'expectedPrice':
      case 'webPrice':
      case 'pluginRaioXPrice':
      case 'dossierPrice':
      case 'agreedPrice':
      case 'bestBid':
      case 'leadPrice':
      case 'tradeInMarketPrice':
      case 'tradeInDealerCosts':
      case 'tradeInExpectedPrice':
        return this.formatCurrency(value);

      case 'km':
        return this.formatKm(value);

      case 'scheduledDate':
      case 'expirationDate':
      case 'boughtAt':
      case 'createdAt':
      case 'leadPriceExpirationDate':
      case 'fastQuoteExpiration':
        return this.formatDate(value);

      case 'doors':
      case 'passengers':
      case 'power':
      case 'gearsSize':
      case 'tankSize':
      case 'manufactureYear':
      case 'modelYear':
      case 'stockDays':
        return String(value);

      case 'grossMargin':
      case 'margin':
        return this.formatPercentage(value);

      case 'questions':
        return this.formatQuestions(value);

      case 'axleAndTire':
        return this.formatAxleAndTire(value);

      case 'implement':
        return this.formatImplement(value);

      case 'commentsForPricing':
        return this.formatCommentsForPricing(value);

      case 'equipmentIds':
      case 'originalEquipmentIds':
        if (Array.isArray(value)) {
          return value.length > 0 ? `${value.length} item(ns)` : 'Nenhum';
        }
        return 'Não informado';

      case 'truckCabin':
        return value;

      case 'truckClassificationLabel':
        return TruckClassifications[value] || value;

      case 'truckTractionLabel':
        return TruckTractions[value] || value;

      case 'truckTypeLabel':
        return TruckType[value] || value;

      default:
        return this.formatDefaultValue(value);
    }
  }

  private getStatusLabel(value: string): string {
    const statusLabels: { [key: string]: string } = {
      'BOUGHT': 'Comprado',
      'ARCHIVED': 'Arquivado',
      'EXPIRED': 'Expirada',
      'SOLD': 'Vendido',
      'DELETED': 'Excluído',
      'CANCELLED': 'Cancelado'
    };
    return statusLabels[value] || EvaluationStatusSelectedOptions[value] || value;
  }

  private getSegmentLabel(value: string): string {
    const segmentLabels: { [key: string]: string } = {
      'CAR': 'Carro',
      'TRUCK': 'Caminhão',
      'MOTORCYCLE': 'Moto'
    };
    return segmentLabels[value] || value;
  }

  private formatCurrency(value: any): string {
    if (typeof value !== 'number') {
      const parsed = parseFloat(value);
      if (isNaN(parsed)) return 'Não informado';
      value = parsed;
    }
    try {
      return this.currencyPipe.transform(value, 'BRL', 'symbol', '1.2-2') || `R$ ${value.toFixed(2)}`;
    } catch {
      return `R$ ${value.toFixed(2)}`;
    }
  }

  private formatCurrencyCent(value: any): string {
    if (typeof value !== 'number') {
      const parsed = parseFloat(value);
      if (isNaN(parsed)) return 'Não informado';
      value = parsed;
    }

    const price = toPriceDisplayValue(value);
    if (price === null) return 'Não informado';

    return this.formatCurrency(price);
  }

  private formatKm(value: any): string {
    if (typeof value !== 'number') {
      const parsed = parseFloat(value);
      if (isNaN(parsed)) return 'Não informado';
      value = parsed;
    }
    return `${this.decimalPipe.transform(value, '1.0-0')} km`;
  }

  private formatDate(value: any): string {
    if (!value) return 'Não informado';
    try {
      return this.datePipe.transform(value, 'dd/MM/yyyy') || 'Data inválida';
    } catch {
      return String(value);
    }
  }

  private formatPercentage(value: any): string {
    if (typeof value !== 'number') {
      const parsed = parseFloat(value);
      if (isNaN(parsed)) return 'Não informado';
      value = parsed;
    }
    return `${value.toFixed(2)}%`;
  }

  private formatOwner(value: any): string {
    if (typeof value === 'string') return value;
    if (typeof value !== 'object' || value === null) return 'Não informado';
    
    const parts: string[] = [];
    if (value.name) parts.push(value.name);
    if (value.cpf) parts.push(`CPF: ${this.maskCpf(value.cpf)}`);
    if (value.email && !value.name) parts.push(value.email);
    if (value.phoneNumber && !value.name) parts.push(value.phoneNumber);
    
    return parts.length > 0 ? parts.join(' - ') : 'Dados do proprietário atualizados';
  }

  private maskCpf(cpf: any): string {
    if (!cpf) return '';
    const cpfStr = String(cpf).replace(/\D/g, '');
    if (cpfStr.length < 11) return cpfStr;
    return `***.***.${cpfStr.substring(6, 9)}-**`;
  }

  private formatReportItems(value: any): string {
    if (!value) return 'Não informado';
    if (typeof value === 'object') {
      const items = Object.entries(value)
        .map(([key, val]: [string, any]) => {
          const label = EquipmentClassification[val?.classification] || val?.classification;
          return label ? `${key}: ${label}` : key;
        })
        .slice(0, 5);
      if (Object.keys(value).length > 5) {
        items.push(`+${Object.keys(value).length - 5} item(ns)`);
      }
      return items.join(', ') || 'Sem itens';
    }
    return String(value);
  }

  private formatTireReports(value: any): string {
    if (!Array.isArray(value) || value.length === 0) return 'Não informado';
    
    const classificationLabels: { [key: string]: string } = {
      'GOOD': 'Bom',
      'REGULAR': 'Regular',
      'BAD': 'Ruim',
      'NON_EXISTENT': 'Inexistente'
    };
    
    const counts = value.reduce((acc: { [key: string]: number }, tire: any) => {
      const label = classificationLabels[tire.classification] || tire.classification;
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {});
    
    return Object.entries(counts)
      .map(([label, count]) => `${count} ${label}`)
      .join(', ');
  }

  private formatQuestions(value: any): string {
    if (!value || typeof value !== 'object') return 'Não informado';
    
    const questionLabels: { [key: string]: string } = {
      'dateLastRevision': 'Data última revisão',
      'involvedInAccident': 'Envolvido em acidente',
      'primaryUse': 'Uso principal',
      'retouchedBodywork': 'Lataria retocada',
      'vehicleOwnership': 'Propriedade do veículo',
      'revisionInDealership': 'Revisão na concessionária',
      'odometerReplacement': 'Hodômetro substituído',
      'maintenanceBudget': 'Orçamento de manutenção',
      'estimatedMarketValue': 'Valor estimado de mercado'
    };
    
    const filledQuestions = Object.entries(value)
      .filter(([_, val]) => val !== null && val !== undefined && val !== '')
      .map(([key, val]) => questionLabels[key] || key);
    
    return filledQuestions.length > 0 
      ? `${filledQuestions.length} pergunta(s) respondida(s)` 
      : 'Nenhuma pergunta respondida';
  }

  private formatAxleAndTire(value: any): string {
    if (!value || typeof value !== 'object') return 'Não informado';
    
    const parts: string[] = [];
    if (value.axles && Array.isArray(value.axles)) {
      parts.push(`${value.axles.length} eixo(s)`);
    }
    if (value.stepQuantity) parts.push(`${value.stepQuantity} etapas`);
    if (value.tiresDimension) parts.push(`Pneus: ${value.tiresDimension}`);
    if (value.wheelbase) parts.push(`Entreeixos: ${value.wheelbase}mm`);
    
    return parts.length > 0 ? parts.join(', ') : 'Configuração atualizada';
  }

  private formatImplement(value: any): string {
    if (!value || typeof value !== 'object') return 'Não informado';
    
    const parts: string[] = [];
    if (value.typeLabel) {
      parts.push(value.typeLabel);
    } else if (value.type) {
      parts.push(ImplementTypeName[value.type] || value.type);
    }
    if (value.makeName) parts.push(value.makeName);
    
    return parts.length > 0 ? parts.join(' - ') : 'Implemento atualizado';
  }

  private formatCommentsForPricing(value: any): string {
    if (!Array.isArray(value) || value.length === 0) return 'Não informado';
    return `${value.length} comentário(s)`;
  }

  private formatDefaultValue(value: any): string {
    if (Array.isArray(value)) {
      if (value.length === 0) return 'Nenhum';
      if (typeof value[0] === 'object') {
        return `${value.length} item(ns)`;
      }
      return value.join(', ');
    }
    
    if (typeof value === 'object' && value !== null) {
      if (value.name) return value.name;
      if (value.label) return value.label;
      if (value.description) return value.description;
      if (value.id && Object.keys(value).length === 1) return value.id;
      return 'Dados atualizados';
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }
    
    return String(value);
  }
}
